import { FC, useRef } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { ClickAwayListener, Grid, Paper, Popper, Stack, Typography, useMediaQuery, CardActions, Button, PopperProps } from '@mui/material';

// third-party

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

// assets
import { IconInfoCircle } from '@tabler/icons';
import usePolling from 'hooks/usePolling';
import { useTranslation } from 'react-i18next';
import NotificationService from 'services/NotificationService';
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import { openAlert, setIsProgress } from 'store/slices/notification';

interface AlertProgressPopperProps extends PopperProps {}

const AlertProgressPopper: FC<AlertProgressPopperProps> = ({ open = false, ...rest }) => {
    const theme = useTheme();
    const { user } = useAuth();
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const { alert } = useSelector((state) => state.notification);

    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const hasOpenedPopper = useRef(alert);

    const notificationService = new NotificationService();

    const handleCloseHelper = () => {
        dispatch(openAlert(false));
    };

    usePolling(async () => {
        if (!user) return;

        try {
            const res = await notificationService.isProgressNotification(user.tenantId);
            dispatch(setIsProgress(res));
            if (res && !hasOpenedPopper.current) {
                dispatch(openAlert(true));
                hasOpenedPopper.current = true;
            }
        } catch (error) {
            console.log(error);
        }
    }, 20000);

    return (
        <Popper
            {...rest}
            placement={matchesXs ? 'bottom' : 'bottom-end'}
            open={alert}
            transition
            disablePortal
            popperOptions={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 20]
                        }
                    }
                ]
            }}
        >
            {({ TransitionProps }) => (
                <ClickAwayListener onClickAway={handleCloseHelper}>
                    <Transitions position={matchesXs ? 'top' : 'top-right'} in={alert} {...TransitionProps}>
                        <Paper>
                            {alert && (
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Grid container direction="column" spacing={2} sx={{ pt: 2, px: 2, maxWidth: '400px' }}>
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <IconInfoCircle size="24px" stroke={2} />
                                                <Typography variant="h4">{t('A_PROCESS_IS_CURRENTLY_RUNNING')}</Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" fontWeight="200">
                                                {t('A_PROCESS_IS_CURRENTLY_RUNNING_MESSAGE')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <CardActions sx={{ p: 1.25, justifyContent: 'right' }}>
                                        <Button variant="contained" size="small" disableElevation onClick={handleCloseHelper}>
                                            Ok
                                        </Button>
                                    </CardActions>
                                </MainCard>
                            )}
                        </Paper>
                    </Transitions>
                </ClickAwayListener>
            )}
        </Popper>
    );
};

export default AlertProgressPopper;
